import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ActiveGuest } from '../../../../interfaces/property-visit.interface';
import { Modules, RouteParamsTypes } from '../../../../enums';

const PLATFORM_META: Record<string, { label: string; badge: string }> = {
  airbnb: { label: 'Airbnb', badge: 'danger' },
  vrbo: { label: 'VRBO', badge: 'primary' },
  booking: { label: 'Booking.com', badge: 'info' },
  direct: { label: 'Direct', badge: 'success' },
};

interface GuestAlertIconProps {
  guest: ActiveGuest;
}

const GuestAlertIcon: React.FC<GuestAlertIconProps> = ({ guest }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const platform = PLATFORM_META[guest.platform] ?? { label: guest.platform, badge: 'secondary' };

  const reservationUrl = guest.reservationId
    ? `${Modules.reservations}${RouteParamsTypes.view}/${guest.reservationId}`
    : null;

  return (
    <div ref={containerRef} style={{ position: 'relative', flexShrink: 0 }}>
      <span
        onClick={() => setOpen((p) => !p)}
        title={t('propertyVisits.guestAlert.title')}
        className="text-primary"
        style={{ fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
      >
        <i className="ri-calendar-check-line ri-26px"></i>
      </span>

      {open && (
        <div
          className="card shadow-lg"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 250,
            zIndex: 1050,
            border: '1px solid #e0e0e0',
          }}
        >
          <div
            className="card-header py-2 px-3 d-flex align-items-center justify-content-between"
            style={{ background: 'var(--bs-primary-bg-subtle)', borderBottom: '1px solid var(--bs-primary-border-subtle)' }}
          >
            <span className="fw-semibold small text-primary">
              <i className="ri-calendar-check-line me-1"></i>
              {t('propertyVisits.guestAlert.title')}
            </span>
            <i
              className="ri-close-line cursor-pointer text-muted"
              onClick={() => setOpen(false)}
              style={{ fontSize: 16 }}
            ></i>
          </div>

          <div className="card-body p-3">
            <table className="w-100" style={{ fontSize: 13, borderCollapse: 'separate', rowGap: 4 }}>
              <tbody>
                <PopoverRow label={t('propertyVisits.guestAlert.guestName')} value={guest.guestName} />
                <tr>
                  <td className="text-muted pe-2 py-1" style={{ whiteSpace: 'nowrap' }}>
                    {t('propertyVisits.guestAlert.platform')}
                  </td>
                  <td className="py-1">
                    <span className={`badge bg-label-${platform.badge}`}>{platform.label}</span>
                  </td>
                </tr>
                <PopoverRow label={t('propertyVisits.guestAlert.checkIn')} value={guest.checkIn} />
                <PopoverRow label={t('propertyVisits.guestAlert.checkOut')} value={guest.checkOut} />
                <PopoverRow label={t('propertyVisits.guestAlert.nights')} value={String(guest.nights)} />
                <tr>
                  <td colSpan={2} className="pt-1 pb-0">
                    <hr className="my-1" />
                  </td>
                </tr>
                <tr>
                  <td colSpan={2} className="pt-1">
                    {reservationUrl ? (
                      <a
                        href={reservationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary small d-flex align-items-center gap-1"
                      >
                        <i className="ri-external-link-line"></i>
                        {t('propertyVisits.guestAlert.viewReservation')}
                      </a>
                    ) : (
                      <span className="text-muted small d-flex align-items-center gap-1">
                        <i className="ri-external-link-line"></i>
                        {t('propertyVisits.guestAlert.viewReservation')}
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const PopoverRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <tr>
    <td className="text-muted pe-2 py-1" style={{ whiteSpace: 'nowrap' }}>{label}</td>
    <td className="fw-semibold py-1">{value}</td>
  </tr>
);

export default GuestAlertIcon;
